// Board-side outcome naming — the event view's primitives, poorer input.
//
// Not identical rules: the event view drops colour whenever both sides render
// (isNeutralNoSide), while here green/red survives a generic negative and only
// a two-name pair switches to the team palette. See sideColor.ts.
//
// The board endpoint ships two flat columns instead of the raw outcomes pair.
// outcome_no_label is outcomes[1] and can be trusted. outcome_yes_label is
// Polymarket's groupItemTitle, which is the yes outcome on some shapes and the
// market's own title on others:
//
//   dependent    'Oprah Winfrey' / 'No'        title is the candidate
//   single       'Up'            / 'Down'      title is the outcome
//   independent  'Match Winner'  / 'Weibo Gaming'   title is the MARKET
//
// So outcome_yes_label is never read at all. The yes side is the fetched pair's
// first outcome, else the counterpart of a directional no-label ("Under" gives
// "Over"), else YES. A dependent market's sides really are yes and no — the
// candidate's name belongs to the market, and the row label already shows it.
//
// /api/event-markets carries the true pair and overrides all of this when it
// lands; needsTrueOutcomes says when that request is worth making.

import {
    directionalCounterpart,
    isDirectionalPair,
    isGenericNegative,
    isNeutralNoSide,
    outcomeLabel,
    type OutcomeLabel,
} from '../../utils/outcomeLabels';

/**
 * The one label column this module reads, carried by both ScreenerMarket and
 * BoardScore. outcome_yes_label is deliberately absent: it is groupItemTitle,
 * and nothing here is willing to treat that as an outcome name.
 */
export interface OutcomeLabelSource {
    outcome_no_label?: string | null;
}

export interface SideLabelOptions {
    /** The market's true outcome pair, once /api/event-markets has landed. */
    outcomes?: string[] | null;
}

export interface SideLabels {
    yes: OutcomeLabel;
    no: OutcomeLabel;
    /** True when the sides are two real names, so they rank as teams, not as right/wrong. */
    namedPair: boolean;
    /** True when either side resolved to something other than YES/NO. */
    named: boolean;
}

const YES_FALLBACK: OutcomeLabel = { text: 'YES', title: 'YES' };
const NO_FALLBACK: OutcomeLabel = { text: 'NO', title: 'NO' };

/** One side's label under the shared naming rules, or the fallback. */
function labelFor(
    name: string | null | undefined,
    fallback: OutcomeLabel,
    maxLen: number
): OutcomeLabel {
    return outcomeLabel([name ?? '', ''], 0, maxLen) ?? fallback;
}

/** True when either side says more than the YES/NO it would have said anyway. */
function isNamed(yes: OutcomeLabel, no: OutcomeLabel): boolean {
    return yes.text !== 'YES' || no.text !== 'NO';
}

/** A usable two-outcome pair, or null when the caller must fall back. */
function wellFormedPair(outcomes: string[] | null | undefined): string[] | null {
    if (!outcomes || outcomes.length !== 2) return null;
    const ok = outcomes.every(o => typeof o === 'string' && o.trim() !== '');
    return ok ? outcomes : null;
}

/**
 * True when only /api/event-markets can name the yes side: the no side is a real
 * name, so this is a head-to-head and outcome_yes_label is the market's title.
 * Every other shape is nameable from the board payload alone — no request needed.
 */
export function needsTrueOutcomes(source: OutcomeLabelSource | null | undefined): boolean {
    const no = source?.outcome_no_label;
    return typeof no === 'string' && no.trim() !== '' && !isGenericNegative(no);
}

export function sideLabels(
    source: OutcomeLabelSource | null | undefined,
    maxLen: number,
    opts: SideLabelOptions = {}
): SideLabels {
    const pair = wellFormedPair(opts.outcomes);

    // The true pair wins outright; a literal Yes/No pair simply renders YES/NO,
    // and is no more "named" than the fallback that produces the same words.
    if (pair) {
        const yes = outcomeLabel(pair, 0, maxLen) ?? YES_FALLBACK;
        const no = outcomeLabel(pair, 1, maxLen) ?? NO_FALLBACK;
        return { yes, no, namedPair: isNeutralNoSide(pair), named: isNamed(yes, no) };
    }

    const noName = source?.outcome_no_label;
    const no = labelFor(noName, NO_FALLBACK, maxLen);
    // Derived beats declared: "Under" tells us the yes side is "Over" outright.
    const counterpart = directionalCounterpart(noName);
    const yes = counterpart ? labelFor(counterpart, YES_FALLBACK, maxLen) : YES_FALLBACK;

    return {
        yes,
        no,
        // The no side carrying a real name is both what makes this a team pair
        // and what makes the request worth it — one predicate, not two.
        namedPair: needsTrueOutcomes(source),
        named: isNamed(yes, no),
    };
}
